<template>
  <div class="app-container flow-form-page">
    <!-- 顶部 -->
    <div class="form-header">
      <div class="header-left">
        <el-button size="small" icon="el-icon-back" plain @click="goBack">返回</el-button>
        <span class="form-title">{{ pageTitle }}</span>
      </div>
      <div class="header-right">
        <el-tag v-if="flowInc && flowInc.status" :type="statusType(flowInc.status)" effect="dark" size="small">
          {{ statusText(flowInc.status) }}
        </el-tag>
      </div>
    </div>

    <div class="form-body" v-loading="loading">
      <el-alert
        v-if="mode === 'start' && flowIncId && !canEditDraft"
        title="该流程已不是您在开始节点的待办任务，请在「待办」中审批或通过列表点击查看。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      />
      <!-- 表单区块 -->
      <el-form ref="form" :model="formData" label-position="top" class="approval-form" size="medium">
        <el-card v-for="group in visibleGroups" :key="group.name" class="form-section" shadow="never">
          <div slot="header" class="section-header">
            <span class="section-title">{{ group.title }}</span>
          </div>

          <el-row :gutter="24">
            <template v-for="field in group.fields">
              <el-col v-if="isFieldVisible(group.name, field.name)" :key="field.name" :span="24">
                <div class="field-wrapper">
                  <form-item-render
                    :field="field"
                    :value="getFieldValue(group.name, field.name)"
                    :readonly="!isFieldEditable(group.name, field.name)"
                    :required="isFieldRequired(group.name, field.name)"
                    :prop="group.name + '.' + field.name"
                    :flow-inc="flowInc"
                    :form-data="formData"
                    :logs="logs"
                    @input="val => setFieldValue(group.name, field.name, val)"
                  />
                </div>
              </el-col>
            </template>
          </el-row>
        </el-card>
      </el-form>

      <!-- 审批操作区 -->
      <el-card v-if="canOperate" class="form-section action-section" shadow="always">
        <div slot="header" class="section-header">
          <span class="section-title">{{ approveOperateSectionTitle }}</span>
        </div>
        <el-form label-position="top">
          <el-form-item label="审批意见" style="margin-bottom: 20px;">
            <el-input
              v-model="comment"
              type="textarea"
              :rows="4"
              placeholder="请输入您的审批意见..."
              maxlength="500"
              show-word-limit
            />
          </el-form-item>
          <div class="action-buttons">
            <el-button v-if="canSubmit" type="success" icon="el-icon-check" :loading="submitting" :disabled="submitting" @click="handleAction('yes')">同意</el-button>
            <el-button v-if="canReject && rejectOptions.length === 1" type="warning" icon="el-icon-close" :disabled="submitting" @click="handleReject(rejectOptions[0])">退回</el-button>
            <el-dropdown v-if="canReject && rejectOptions.length > 1" trigger="click" @command="handleReject">
              <el-button type="warning" icon="el-icon-close" :disabled="submitting">退回 <i class="el-icon-arrow-down el-icon--right"></i></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-for="opt in rejectOptions" :key="opt.action" :command="opt">退回至【{{ opt.title }}】</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button v-if="canEnd" type="danger" icon="el-icon-delete" :disabled="submitting" @click="handleAction('end')">终止</el-button>
            <el-button plain icon="el-icon-document" :loading="saving" @click="handleSaveFormDraft">保存</el-button>
          </div>
        </el-form>
      </el-card>

      <!-- 发起提交按钮 -->
      <div v-if="mode === 'start' && showStartActions" class="submit-area">
        <el-button size="large" icon="el-icon-document" :loading="saving" @click="handleSaveFormDraft">保存</el-button>
        <el-button type="primary" size="large" icon="el-icon-s-promotion" :loading="submitting" @click="handleStart">提交申请</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { getFlowFormDef, getAvailableFlows, startFlow, getFlowIncDetail, submitFlow, saveFlowForm } from '../../api/workflow'
import FormItemRender from '../../components/workflow/FormItemRender'

export default {
  name: 'ApprovalForm',
  components: { FormItemRender },
  data() {
    return {
      mode: 'start',
      flowCode: '', // 流程编码，发起时用来查找流程定义
      flowDefId: null,
      flowIncId: null,
      loading: false,
      submitting: false,
      saving: false,
      /** 发起模式下带流程实例草稿时：与后端一致，仅 pending 办理人可编辑 */
      canEditDraft: true,
      flowInc: null,
      formDef: null,
      formGroups: [],
      formData: {},
      logs: [],
      canOperate: false,
      currentNodeConfig: null,
      comment: '',
      fieldsAccess: {},
      nodesTitleMap: {}
    }
  },
  computed: {
    /** 流程节点 def_json 中可选 actionLabel（如「用印」）；未配置时沿用「审批处理」 */
    approveOperateSectionTitle() {
      const w = this.currentNodeConfig && this.currentNodeConfig.actionLabel
      const s = w != null ? String(w).trim() : ''
      if (s) return s + '处理'
      return '审批处理'
    },
    pageTitle() {
      const title = this.flowInc ? this.flowInc.flowTitle : ''
      if (this.mode === 'start') return '发起审批' + (title ? ' - ' + title : '')
      if (this.mode === 'approve') return this.approveOperateSectionTitle + (title ? ' - ' + title : '')
      return '审批详情' + (title ? ' - ' + title : '')
    },
    canSubmit() {
      return this.currentNodeConfig && this.currentNodeConfig.canSubmit
    },
    canReject() {
      return this.currentNodeConfig && this.currentNodeConfig.canReject
    },
    canEnd() {
      return this.currentNodeConfig && this.currentNodeConfig.canEnd
    },
    showStartActions() {
      if (this.mode !== 'start') return false
      if (!this.flowIncId) return true
      return this.canEditDraft
    },
    // 解析可回退的目标节点列表
    rejectOptions() {
      if (!this.currentNodeConfig || !this.currentNodeConfig.gotos) return []
      const options = []
      const gotos = this.currentNodeConfig.gotos
      for (const g of gotos) {
        const cond = g.conditionText || ''
        // 找 $status='xxx' 格式的条件，排除 yes 和 end
        if (cond.includes('$status')) {
          // 提取条件值
          const match = cond.match(/\$status\s*=\s*['"]?(\w+)['"]?/)
          if (match) {
            const statusVal = match[1]
            if (statusVal !== 'yes' && statusVal !== 'end') {
              // 优先用分支配置的 name，其次用节点的 title（从 nodesTitleMap 查找），最后用 to
              const targetTitle = g.name || this.nodesTitleMap[g.to] || g.to
              options.push({
                to: g.to,
                title: targetTitle,
                action: statusVal
              })
            }
          }
        }
      }
      return options
    },
    visibleGroups() {
      return this.formGroups.filter(g => this.isGroupVisible(g.name))
    }
  },
  created() {
    this.mode = this.$route.query.mode || 'start'
    this.flowCode = this.$route.query.flowCode || ''
    this.flowDefId = this.$route.query.flowDefId || null
    this.flowIncId = this.$route.query.flowIncId || null

    if (this.mode === 'start') {
      if (this.flowIncId) {
        this.loadFlowDetail()
      } else if (this.flowDefId) {
        this.loadFormDef()
      } else if (this.flowCode) {
        this.loadFormDefByCode()
      }
    } else if (this.flowIncId) {
      this.loadFlowDetail()
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },

    // ========== 数据加载 ==========
    async loadFormDefByCode() {
      // 通过流程编码找到流程定义ID，再加载表单
      this.loading = true
      try {
        const res = await getAvailableFlows()
        const flows = res.data || []
        const matched = flows.find(f => f.code === this.flowCode)
        if (!matched) {
          this.$message.error('未找到对应的流程定义：' + this.flowCode)
          this.loading = false
          return
        }
        this.flowDefId = matched.id
        await this.loadFormDef()
      } catch (e) {
        this.$message.error('加载流程定义失败')
        this.loading = false
      }
    },
    async loadFormDef() {
      this.loading = true
      try {
        const res = await getFlowFormDef(this.flowDefId)
        const data = res.data
        this.formDef = data.form
        this.formGroups = (data.form && data.form.fields) || []
        // 初始化默认值
        this.formGroups.forEach(group => {
          if (group.fields) {
            group.fields.forEach(field => {
              if (field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '') {
                this.$set(this.formData, group.name + '.' + field.name, field.defaultValue)
              }
            })
          }
        })
        if (data.startNodeConfig && data.startNodeConfig.fieldsAccess) {
          this.fieldsAccess = data.startNodeConfig.fieldsAccess
        }
        this.currentNodeConfig = data.startNodeConfig || {}
        this.canOperate = false
        this.canEditDraft = true
        this.flowInc = { flowTitle: data.flowDef ? data.flowDef.title : '' }
      } catch (e) {
        this.$message.error('加载表单定义失败')
      }
      this.loading = false
    },
    async loadFlowDetail() {
      this.loading = true
      try {
        const res = await getFlowIncDetail(this.flowIncId)
        const data = res.data
        this.flowInc = data.flowInc
        if (this.flowInc && this.flowInc.flowDefId != null && (this.flowDefId == null || this.flowDefId === '')) {
          this.flowDefId = this.flowInc.flowDefId
        }
        this.logs = data.logs || []
        this.canOperate = data.canOperate && this.mode === 'approve'
        if (this.mode === 'start') {
          this.canEditDraft = !!data.canOperate
        } else {
          this.canEditDraft = false
        }
        this.currentNodeConfig = data.currentNodeConfig
        if (data.formDef) {
          this.formDef = data.formDef
          this.formGroups = (data.formDef && data.formDef.fields) || []
        }
        if (this.currentNodeConfig && this.currentNodeConfig.fieldsAccess) {
          this.fieldsAccess = this.currentNodeConfig.fieldsAccess
        }
        if (data.formData) {
          try {
            const parsed = JSON.parse(data.formData)
            this.formData = this.flattenFormData(parsed)
          } catch (_e) {
            this.formData = {}
          }
        }
        // 节点名 -> 标题映射，用于显示回退目标的中文名称
        if (data.nodesTitleMap) {
          this.nodesTitleMap = data.nodesTitleMap
        }
      } catch (e) {
        this.$message.error('加载流程详情失败')
      }
      this.loading = false
    },

    // ========== 表单权限 ==========
    /**
     * 解析 fieldsAccess（与设计器 FlowDesigner 一致：组维度 + fields 下子字段维度）。
     * 子字段上若显式配置了某属性则以子字段为准，否则继承组；组上无时 visible 默认为 true。
     */
    _effectiveAccessBool(groupName, fieldName, prop, defaultWhenNoGroup) {
      const ga = this.fieldsAccess[groupName]
      if (!ga) return defaultWhenNoGroup

      const fa = fieldName && ga.fields ? ga.fields[fieldName] : null
      if (fa && Object.prototype.hasOwnProperty.call(fa, prop)) {
        return !!fa[prop]
      }
      if (Object.prototype.hasOwnProperty.call(ga, prop)) {
        return !!ga[prop]
      }
      if (prop === 'visible') return true
      return false
    },
    isGroupVisible(groupName) {
      return this._effectiveAccessBool(groupName, null, 'visible', true)
    },
    isFieldVisible(groupName, fieldName) {
      if (!this.isGroupVisible(groupName)) return false
      return this._effectiveAccessBool(groupName, fieldName, 'visible', true)
    },
    isFieldEditable(groupName, fieldName) {
      if (this.mode === 'view') return false
      if (this.mode === 'start' && this.flowIncId && !this.canEditDraft) return false
      const ga = this.fieldsAccess[groupName]
      if (!ga && this.mode === 'start') return true
      if (!ga) return false
      return this._effectiveAccessBool(groupName, fieldName, 'editable', false)
    },
    isFieldRequired(groupName, fieldName) {
      const ga = this.fieldsAccess[groupName]
      if (!ga) return false
      return this._effectiveAccessBool(groupName, fieldName, 'verifiable', false)
    },

    /**
     * 按流程配置的 verifiable 判断当前值是否视为「未填写」。（用于提交前校验，不依赖 Element 的 rules/model 路径）
     */
    isVerifiableValueEmpty(groupName, fieldName, raw, fieldDef) {
      const field = fieldDef || {}
      const component = field.component
      const inputType = field.inputType

      // 审批记录不参与表单值，不写 formData
      if (component === 'FlowLogs') {
        return false
      }

      if (raw === null || raw === undefined) return true
      if (typeof raw === 'string' && raw.trim() === '') return true

      if (component === 'SealSelector') {
        if (typeof raw === 'object' && !Array.isArray(raw)) {
          return !Object.values(raw).some(v => !!v)
        }
        return !Array.isArray(raw) || raw.length === 0
      }
      if (component === 'TemplateFiles') {
        return !Array.isArray(raw) || raw.length === 0
      }
      if (component === 'RiskControlOperator') {
        return raw === '' || raw === null || raw === undefined
      }

      // 其余自定义组件：尽量宽松地从对象 / 数组判断是否有实质内容
      if (field.type === 'Component' && component) {
        if (Array.isArray(raw)) return raw.length === 0
        if (typeof raw === 'object' && raw !== null) {
          return !Object.keys(raw).some(k => {
            const v = raw[k]
            if (v === null || v === undefined) return false
            if (typeof v === 'boolean') return v
            if (typeof v === 'string') return v.trim() !== ''
            if (typeof v === 'number') return !Number.isNaN(v)
            if (Array.isArray(v)) return v.length > 0
            if (typeof v === 'object') return Object.keys(v).length > 0
            return !!v
          })
        }
        return false
      }

      if (inputType === 'number') {
        return raw === '' || Number.isNaN(Number(raw))
      }
      // 开关勾选「必填」时：仍以是否明确选择为准（true/false 均视为已选）
      if (inputType === 'switch') {
        return false
      }
      if (Array.isArray(raw)) {
        return raw.length === 0
      }

      return false
    },

    /** 校验所有「当前节点配置的 verifiable 且可见」的字段是否已填写 */
    validateVerifiableFieldsBeforeSubmit() {
      const titles = []
      for (const group of this.visibleGroups) {
        if (!group.fields) continue
        for (const field of group.fields) {
          const g = group.name
          const fname = field.name
          if (!this.isFieldVisible(g, fname)) continue
          if (!this.isFieldRequired(g, fname)) continue
          const val = this.getFieldValue(g, fname)
          if (this.isVerifiableValueEmpty(g, fname, val, field)) {
            titles.push(field.title || fname)
          }
        }
      }
      if (titles.length === 0) return true
      const max = 5
      const head = titles.slice(0, max).join('、')
      const more = titles.length > max ? ` 等 ${titles.length} 项` : ''
      this.$message.error(`请填写必填项：${head}${more}`)
      return false
    },

    // ========== 表单数据 ==========
    getFieldValue(groupName, fieldName) {
      return this.formData[groupName + '.' + fieldName]
    },
    setFieldValue(groupName, fieldName, val) {
      this.$set(this.formData, groupName + '.' + fieldName, val)
    },
    flattenFormData(obj, prefix, depth) {
      depth = depth || 0
      const result = {}
      for (const key in obj) {
        const fullKey = prefix ? prefix + '.' + key : key
        // 只展开第一层（组名），第二层（字段值）保持原样，不管是对象还是数组
        if (depth < 1 && obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          Object.assign(result, this.flattenFormData(obj[key], fullKey, depth + 1))
        } else {
          result[fullKey] = obj[key]
        }
      }
      return result
    },
    unflattenFormData(flat) {
      const result = {}
      for (const key in flat) {
        const parts = key.split('.')
        let cur = result
        for (let i = 0; i < parts.length - 1; i++) {
          if (!cur[parts[i]]) cur[parts[i]] = {}
          cur = cur[parts[i]]
        }
        cur[parts[parts.length - 1]] = flat[key]
      }
      return result
    },

    // ========== 操作 ==========
    async handleSaveFormDraft() {
      const formDataObj = this.unflattenFormData(this.formData)
      const json = JSON.stringify(formDataObj)

      if (this.mode === 'approve') {
        if (!this.canOperate) return
        this.saving = true
        try {
          await saveFlowForm({ flowIncId: Number(this.flowIncId), formData: json })
          this.$message.success('已保存')
        } catch (e) {
          this.$message.error(e.response && e.response.data ? e.response.data.msg : '保存失败')
        }
        this.saving = false
        return
      }

      // 发起：无实例则创建草稿；已有则只更新表单数据
      if (this.flowIncId && !this.canEditDraft) return
      if (!this.flowDefId && !this.flowIncId) {
        this.$message.error('缺少流程定义，无法保存')
        return
      }

      this.saving = true
      try {
        let id = this.flowIncId ? Number(this.flowIncId) : null
        if (!id) {
          const res = await startFlow({
            flowDefId: Number(this.flowDefId),
            formData: json
          })
          id = res.data.id
          this.flowIncId = id
          this.flowInc = res.data
          this.canEditDraft = true
          await this.$router.replace({
            path: this.$route.path,
            query: { ...this.$route.query, flowIncId: String(id), mode: 'start' }
          })
          await this.loadFlowDetail()
        } else {
          await saveFlowForm({ flowIncId: id, formData: json })
        }
        this.$message.success('已保存')
      } catch (e) {
        this.$message.error(e.response && e.response.data ? e.response.data.msg : '保存失败')
      }
      this.saving = false
    },
    async handleStart() {
      if (this.flowIncId && !this.canEditDraft) {
        this.$message.warning('当前不可提交')
        return
      }
      if (!this.validateVerifiableFieldsBeforeSubmit()) return
      this.submitting = true
      try {
        const formDataObj = this.unflattenFormData(this.formData)
        const json = JSON.stringify(formDataObj)
        let id = this.flowIncId ? Number(this.flowIncId) : null
        if (!id) {
          const res = await startFlow({
            flowDefId: Number(this.flowDefId),
            formData: json
          })
          id = res.data.id
          this.flowIncId = id
          this.flowInc = res.data
        }
        this.$message.success('流程发起成功')
        // 自动提交 start 节点
        await submitFlow({
          flowIncId: id,
          action: 'yes',
          comment: '发起申请',
          formData: json
        })
        this.$message.success('已提交审批')
        this.$router.go(-1)
      } catch (e) {
        this.$message.error(e.response && e.response.data ? e.response.data.msg : '发起失败')
      }
      this.submitting = false
    },
    async handleAction(action) {
      // 防重复提交：请求未返回前拦截再次触发
      if (this.submitting) return
      // 仅「同意」路径按当前节点 verifiable 校验；终止、退回等不拦
      if (action === 'yes' && !this.validateVerifiableFieldsBeforeSubmit()) return
      const actionText = action === 'yes' ? '同意' : action === 'end' ? '终止' : '退回'
      try {
        await this.$confirm('确定要【' + actionText + '】吗？', '提示', {
          confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
        })
      } catch (_e) {
        return
      }
      // 确认弹窗可能被快速双击/回车重复触发，此处再拦一次
      if (this.submitting) return
      this.submitting = true
      try {
        const formDataObj = this.unflattenFormData(this.formData)
        await submitFlow({
          flowIncId: Number(this.flowIncId),
          action: action,
          comment: this.comment,
          formData: JSON.stringify(formDataObj)
        })
        this.$message.success('操作成功')
        this.$router.go(-1)
      } catch (e) {
        this.$message.error(e.response && e.response.data ? e.response.data.msg : '操作失败')
      }
      this.submitting = false
    },
    async handleReject(option) {
      // 防重复提交：请求未返回前拦截再次触发
      if (this.submitting) return
      // 退回必须填写审批意见
      if (!this.comment || !this.comment.trim()) {
        this.$message.warning('退回时必须填写审批意见')
        return
      }
      const targetTitle = option.title || option.to
      try {
        await this.$confirm('确定要退回至【' + targetTitle + '】吗？', '提示', {
          confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
        })
      } catch (_e) {
        return
      }
      // 确认弹窗可能被快速双击/回车重复触发，此处再拦一次
      if (this.submitting) return
      this.submitting = true
      try {
        const formDataObj = this.unflattenFormData(this.formData)
        await submitFlow({
          flowIncId: Number(this.flowIncId),
          action: option.action,
          comment: this.comment,
          formData: JSON.stringify(formDataObj)
        })
        this.$message.success('退回成功')
        this.$router.go(-1)
      } catch (e) {
        this.$message.error(e.response && e.response.data ? e.response.data.msg : '退回失败')
      }
      this.submitting = false
    },
    statusType(s) {
      return s === 'running' ? 'primary' : s === 'finished' ? 'success' : 'danger'
    },
    statusText(s) {
      return s === 'running' ? '进行中' : s === 'finished' ? '已完成' : s === 'terminated' ? '已终止' : s
    }
  }
}
</script>

<style scoped>
.flow-form-page {
  max-width: 1000px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #f5f7fa;
}
.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e6ebf5;
  background: #fff;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  z-index: 10;
}
.header-left {
  display: flex;
  align-items: center;
}
.form-title {
  font-size: 18px;
  font-weight: 600;
  margin-left: 16px;
  color: #303133;
}
.form-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
.approval-form {
  width: 100%;
}
.form-section {
  margin-bottom: 24px;
  border: none;
  border-radius: 8px;
}
.section-header {
  padding: 0;
  border-left: 4px solid #409eff;
  padding-left: 10px;
  line-height: 1;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.field-wrapper {
  margin-bottom: 8px;
}
.action-section {
  border-top: none;
  border: 1px solid #ebeef5;
}
.action-buttons {
  display: flex;
  gap: 16px;
  justify-content: flex-start;
  margin-top: 10px;
}
.submit-area {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  align-items: center;
  padding: 30px 0 50px;
}
/* 滚动条样式优化 */
.form-body::-webkit-scrollbar {
  width: 8px;
}
.form-body::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.form-body::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}
.form-body::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

@media (max-width: 768px) {
  .action-buttons { flex-wrap: wrap; gap: 10px; }
  .action-buttons .el-button { flex: 1; min-width: 0; margin-left: 0 !important; }
  .action-buttons .el-dropdown { flex: 1; }
  .action-buttons .el-dropdown .el-button { width: 100%; }
  .submit-area { flex-direction: column; padding: 20px 0 30px; }
  .submit-area .el-button { width: 100%; }
  .form-section { margin-bottom: 12px; }
  .flow-form-page { padding: 10px; }
}
</style>

<!-- 非 scoped 样式：覆盖 Element UI disabled 状态的颜色，让审批单内容更醒目 -->
<style>
/* 输入框 disabled 文字颜色加深 */
.flow-form-page .el-input.is-disabled .el-input__inner,
.flow-form-page .el-textarea.is-disabled .el-textarea__inner {
  color: #303133;
  -webkit-text-fill-color: #303133;
  background-color: #f9f9fb;
  cursor: default;
}
/* 下拉框 disabled 文字颜色加深 */
.flow-form-page .el-select .el-input.is-disabled .el-input__inner {
  color: #303133;
  -webkit-text-fill-color: #303133;
}
/* 勾选框 disabled 选中状态：背景色加深 */
.flow-form-page .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
  background-color: #409eff;
  border-color: #409eff;
}
.flow-form-page .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner::after {
  border-color: #fff;
}
/* 勾选框 disabled 文字颜色加深 */
.flow-form-page .el-checkbox__input.is-disabled + .el-checkbox__label {
  color: #303133;
}
/* 开关 disabled 选中状态颜色加深 */
.flow-form-page .el-switch.is-disabled.is-checked .el-switch__core {
  background-color: #409eff;
  border-color: #409eff;
}
/* 数字输入框 disabled 文字颜色加深 */
.flow-form-page .el-input-number.is-disabled .el-input__inner {
  color: #303133;
  -webkit-text-fill-color: #303133;
}
/* 日期选择器 disabled 文字颜色加深 */
.flow-form-page .el-date-editor.is-disabled .el-input__inner {
  color: #303133;
  -webkit-text-fill-color: #303133;
}
/* el-descriptions 描述列表内容颜色加深 */
.flow-form-page .el-descriptions-item__content {
  color: #303133;
  font-weight: 500;
}
.flow-form-page .el-descriptions-item__label {
  color: #606266;
  font-weight: 600;
}
/* 表单 label 标签颜色加深 */
.flow-form-page .el-form-item__label {
  color: #303133;
  font-weight: 600;
}
</style>
